import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';

import type {
  FinancialOnboardingState,
  ProductFinanceClient,
} from '#components/product-finance/client';
import { useNavigate } from '#hooks/useNavigate';

import {
  accountOptionStyle,
  actionStyle,
  FormError,
  formStyle,
  getFinancialErrorMessage,
  helpStyle,
  StepCard,
} from './onboarding-shared';

export function AccountsStep({
  onboarding,
  accounts,
  client,
  finishLabel,
  onSaved,
}: Readonly<{
  onboarding: FinancialOnboardingState;
  accounts: readonly AccountEntity[];
  client: ProductFinanceClient;
  finishLabel?: ReactNode;
  onSaved: () => Promise<void>;
}>) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    new Set(
      onboarding.liquidAccountIds.filter(accountId =>
        accounts.some(account => account.id === accountId),
      ),
    ),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selectedIds.size === 0) {
      setError(t('Select at least one liquid account.'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await client.configureLiquidAccounts({ accountIds: [...selectedIds] });
      await onSaved();
    } catch (submitError) {
      setError(getFinancialErrorMessage(submitError, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <StepCard title={<Trans>Select your liquid accounts</Trans>}>
      <p style={helpStyle}>
        <Trans>
          Only selected accounts count as cash available for Safe to Spend.
          Closed accounts are excluded.
        </Trans>
      </p>
      {accounts.length === 0 ? (
        <View style={{ gap: 12 }}>
          <p>
            <Trans>Create a budget account before continuing.</Trans>
          </p>
          <Button onPress={() => navigate('/accounts')} style={actionStyle}>
            <Trans>Go to accounts</Trans>
          </Button>
        </View>
      ) : (
        <form onSubmit={submit} style={formStyle}>
          <View style={{ gap: 8 }}>
            {accounts.map(account => (
              <label key={account.id} style={accountOptionStyle}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(account.id)}
                  onChange={event => {
                    const next = new Set(selectedIds);
                    if (event.target.checked) {
                      next.add(account.id);
                    } else {
                      next.delete(account.id);
                    }
                    setSelectedIds(next);
                  }}
                />
                <span>{account.name}</span>
              </label>
            ))}
          </View>
          <FormError message={error} />
          <ButtonWithLoading
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            style={actionStyle}
          >
            {finishLabel ?? <Trans>Save and continue</Trans>}
          </ButtonWithLoading>
        </form>
      )}
    </StepCard>
  );
}
